// "100% my words" prompt — Sep 29, 2026 (Randy pt-voice-kit).
//
// Randy: "sometimes I slightly edit silo-originated responses. Maybe anytime
// the button is used, when the user goes to save it, a quick pop-up appears:
// flag as 100% original response for voice cadence training?"
//
// So it only appears when the edit was substantial (a light touch-up stays
// silent), and the box arrives already ticked when he rewrote most of it. The
// flag does nothing to the reply — it only tells the Voice Kit which replies it
// may use as a specimen of how he actually answers people.
import { useEffect, useState } from "react";
import { ChatCircleDots, Books } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import DocSaveDialog from "@/components/DocSaveDialog";

// Fraction of the text that changed, 0…1. Cheap LCS-free approximation:
// character-level similarity via a token multiset overlap.
export function editRatio(before, after) {
  const a = (before || "").trim();
  const b = (after || "").trim();
  if (!a) return b ? 1 : 0;
  if (!b) return 1;
  const tok = (s) => s.toLowerCase().split(/\s+/).filter(Boolean);
  const ta = tok(a);
  const tb = tok(b);
  const bag = new Map();
  ta.forEach((t) => bag.set(t, (bag.get(t) || 0) + 1));
  let kept = 0;
  tb.forEach((t) => {
    const n = bag.get(t) || 0;
    if (n > 0) { kept += 1; bag.set(t, n - 1); }
  });
  const denom = Math.max(ta.length, tb.length);
  return denom ? 1 - kept / denom : 0;
}

// Below this, the save is a touch-up and we stay out of the way.
export const ASK_THRESHOLD = 0.25;
// At or above this, he rewrote it — arrive pre-ticked.
export const PRETICK_THRESHOLD = 0.6;

export default function VoiceOriginalPrompt({ open, ratio = 0, onDecide, text = "", siloId = null, siloName = "", silos = null, defaultTitle = "" }) {
  const [checked, setChecked] = useState(ratio >= PRETICK_THRESHOLD);
  // Oct 2026 (Randy) — "anytime this pop-up shows up I would like you to place
  // a send to silo button… the robust version that allows you to send it to any
  // silo." It lives HERE rather than at each call site so every surface that
  // raises this prompt gets it, including ones added later. His follow-up:
  // *"it does not close the window. The user still needs to choose whether or
  // not they want to skip it"* — so the save dialog stacks OVER this one and
  // deciding stays the only way out.
  const [sendOpen, setSendOpen] = useState(false);
  const [siloList, setSiloList] = useState(silos);
  useEffect(() => { if (silos) setSiloList(silos); }, [silos]);
  useEffect(() => {
    // Any surface can raise this prompt without plumbing a silo list through.
    if (!sendOpen || siloList) return;
    let dead = false;
    (async () => {
      try {
        const r = await api.get("/silos");
        if (!dead) setSiloList((r.data?.silos || r.data || []).map((s) => ({ id: s.id, name: s.name })));
      } catch {
        if (!dead) setSiloList(siloId ? [{ id: siloId, name: siloName || "this silo" }] : []);
      }
    })();
    return () => { dead = true; };
  }, [sendOpen, siloList, siloId, siloName]);
  useEffect(() => { if (open) setChecked(ratio >= PRETICK_THRESHOLD); }, [open, ratio]);
  useEffect(() => { if (!open) setSendOpen(false); }, [open]);
  if (!open) return null;
  const pct = Math.round(ratio * 100);
  const canSend = !!(text || "").trim() && !!siloId;
  return (
    <div className="fixed inset-0 z-[120] bg-black/30 backdrop-blur-[2px] flex items-center justify-center p-4" data-testid="voice-original-prompt">
      <div className="bg-[#FDFCF8] border hairline rounded-xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center gap-2">
          <ChatCircleDots size={16} weight="duotone" className="text-[#2F6B3A]" />
          <h3 className="text-[14px] font-semibold text-[#1C1B1A]">Saved. Was that your own writing?</h3>
        </div>
        <p className="text-[12px] text-[#6B6A68] mt-2 leading-relaxed">
          You changed about <strong>{pct}%</strong> of this reply. Flagging it adds it to the pool the
          Voice Kit can pull from as a sample of how you actually answer people. It changes nothing
          about the reply itself, and you can unflag it any time.
        </p>
        <label className="flex items-start gap-2 mt-3 cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="voice-original-checkbox"
            className="mt-0.5 accent-[#2F6B3A]"
          />
          <span className="text-[12px] text-[#1C1B1A]">
            Flag as <strong>100% my words</strong> for voice cadence training
          </span>
        </label>
        <div className="flex items-center justify-between gap-2 mt-4">
          {canSend ? (
            <button
              onClick={() => setSendOpen(true)}
              data-testid="voice-original-send-to-silo"
              title="Save this text into a silo — pick any silo and tier"
              className="text-[11px] px-2.5 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#EAF0EB] hover:border-[#4A5D4E] transition-colors flex items-center gap-1.5 shrink-0"
            >
              <Books size={12} weight="duotone" /> Send to silo
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDecide(null)}
              data-testid="voice-original-skip"
              className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:bg-[#F0F1EE]"
            >
              Skip
            </button>
            <button
              onClick={() => onDecide(checked)}
              data-testid="voice-original-confirm"
              className="text-[11.5px] px-3 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#3A4A3E]"
            >
              {checked ? "Flag it" : "Don't flag"}
            </button>
          </div>
        </div>
      </div>
      {/* Stacks ABOVE this prompt (z-130 beats the z-120 overlay) and closing
          it returns here — the flag decision is still waiting. */}
      {sendOpen && (
        <div className="relative z-[130]">
          <DocSaveDialog
            open
            onClose={() => setSendOpen(false)}
            content={text}
            sourceSiloId={siloId}
            silos={siloList || []}
            defaultTitle={defaultTitle || "My own words"}
            initialTier={3}
            dialogTitle="Send this text to a silo"
            onSaved={() => setSendOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
